import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  NEWS_DESKS,
  NEWS_DESK_LABELS,
  type NewsBoard,
  type NewsItem,
  type NewsRule,
  type NewsState,
  type NewsThread,
} from '@map/shared';
import {
  bulkNews,
  checkThreadNow,
  explainNews,
  fetchNewsBoard,
  followInto,
  fetchNewsTaste,
  markNewsSeen,
  markThreadSeen,
  mergeThreads,
  refreshNews,
  resetNewsTaste,
  saveNewsRules,
  setGroupLabel,
  setThreadTerms,
  splitThread,
  triageNews,
  unfollowThread,
} from './api.js';

/**
 * SAT-06/07/08. The Newswire: what the satellite has found, to be sorted.
 *
 * A Fresh stream ranked by learned taste, each card with three answers —
 * ✕ not interested, ★ interested, ⟳ follow — by click, key (x / i / f, j / k
 * to move, Enter to open, u to undo), drag onto the drop bar on a desktop, or
 * swipe on a phone (left = not interested, right = interested, hold = follow).
 * "Pass on the rest" sends everything left undecided to Not for me at once. Followed stories are threads
 * that collect every development; on a desktop they stay in view beside the
 * stream. The board is whatever is stored, shown at once; a scan, when one is
 * running, fills it in as each desk lands. Headlines come from news feeds
 * (NEWS-02); Explain asks the cheap model once per story and keeps the answer.
 */

type Tab = 'fresh' | 'following' | 'interested' | 'dismissed' | 'taste';

const BOARD_KEY = ['news-board'] as const;
export const NEWS_STATUS_KEY = ['news-status'] as const;

function ago(iso: string | null): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function deskColour(desk: string): string {
  return `var(--desk-${desk})`;
}

export function Newswire({ onClose, mobile = false }: { onClose: () => void; mobile?: boolean }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('fresh');
  const board = useQuery({
    queryKey: BOARD_KEY,
    queryFn: fetchNewsBoard,
    // While a scan runs, cards appear as each desk lands.
    refetchInterval: (q) =>
      (q.state.data as NewsBoard | undefined)?.status.refreshing ? 4000 : 60_000,
  });
  const data = board.data;

  const [undo, setUndo] = useState<{ id: string; previous: NewsState; headline: string }[]>([]);
  const triage = useMutation({
    mutationFn: ({ id, to }: { id: string; to: NewsState }) => triageNews(id, to),
    onMutate: async ({ id, to }) => {
      await queryClient.cancelQueries({ queryKey: BOARD_KEY });
      const before = queryClient.getQueryData<NewsBoard>(BOARD_KEY);
      queryClient.setQueryData<NewsBoard>(BOARD_KEY, (old) =>
        old
          ? {
              ...old,
              items: old.items.map((i) => (i.id === id ? { ...i, state: to, seen: true } : i)),
            }
          : old,
      );
      return { before };
    },
    onError: (_e, _v, ctx) => ctx?.before && queryClient.setQueryData(BOARD_KEY, ctx.before),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
      void queryClient.invalidateQueries({ queryKey: NEWS_STATUS_KEY });
    },
  });

  const act = useCallback(
    (item: NewsItem, to: NewsState) => {
      if (item.state === to) return;
      setUndo((u) =>
        [{ id: item.id, previous: item.state, headline: item.headline }, ...u].slice(0, 20),
      );
      triage.mutate({ id: item.id, to });
    },
    [triage],
  );
  const undoLast = useCallback(() => {
    const [last, ...rest] = undo;
    if (!last) return;
    setUndo(rest);
    triage.mutate({ id: last.id, to: last.previous });
  }, [undo, triage]);

  // Desktop drag and drop: the card being dragged, for the drop bar and the Following column.
  const [dragging, setDragging] = useState<NewsItem | null>(null);
  const dropOn = (to: NewsState) => {
    if (dragging) act(dragging, to);
    setDragging(null);
  };

  // Pass on the rest: every undecided story to Not for me, in one go, with one undo.
  const [confirmPass, setConfirmPass] = useState(false);
  const [passed, setPassed] = useState<string[] | null>(null);
  const bulk = useMutation({
    mutationFn: ({ ids, action }: { ids: string[] | 'all'; action: 'dismissed' | 'fresh' }) =>
      bulkNews(ids, action),
    onSuccess: (r, { ids, action }) =>
      setPassed(action === 'dismissed' ? (ids === 'all' ? (r.ids ?? []) : ids) : null),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
      void queryClient.invalidateQueries({ queryKey: NEWS_STATUS_KEY });
    },
  });

  const refresh = useMutation({
    mutationFn: refreshNews,
    onSettled: () => void queryClient.invalidateQueries({ queryKey: BOARD_KEY }),
  });

  // Seen: a card on screen for 1.5 s has been read, near enough.
  const seenQueue = useRef(new Set<string>());
  const flush = useRef<number | null>(null);
  const markSeen = useCallback(
    (id: string) => {
      seenQueue.current.add(id);
      if (flush.current) return;
      flush.current = window.setTimeout(() => {
        const ids = [...seenQueue.current];
        seenQueue.current.clear();
        flush.current = null;
        void markNewsSeen(ids).then(() =>
          queryClient.invalidateQueries({ queryKey: NEWS_STATUS_KEY }),
        );
      }, 2000);
    },
    [queryClient],
  );

  const items = useMemo(() => data?.items ?? [], [data]);
  const lists = useMemo(
    () => ({
      fresh: items.filter((i) => i.state === 'fresh' && !i.muted),
      muted: items.filter((i) => i.state === 'fresh' && i.muted),
      interested: items.filter((i) => i.state === 'interested'),
      dismissed: items.filter((i) => i.state === 'dismissed'),
    }),
    [items],
  );
  const threads = (data?.threads ?? []).filter((t) => t.active);
  const followingUnseen = threads.reduce((n, t) => n + t.unseen, 0);

  const showSideThreads =
    !mobile && (tab === 'fresh' || tab === 'interested' || tab === 'dismissed');

  const status = data?.status;
  const firstScan = status?.empty && status.refreshing;

  return (
    <div className="flex h-full flex-col" style={{ background: 'var(--surface-1)' }}>
      <header className="flex items-start gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Newswire</h2>
          <p className="text-xs text-text-muted" aria-live="polite">
            {status?.refreshing ? (
              <span style={{ color: 'var(--sat-beacon)' }}>
                ● Fetching latest
                {status.pendingDesks.length > 0 &&
                  ` — ${status.pendingDesks.map((d) => NEWS_DESK_LABELS[d]).join(', ')}`}
                …
              </span>
            ) : status?.lastRunAt ? (
              `Updated ${ago(status.lastRunAt)} ago${status.ai ? '' : ' · AI off'}`
            ) : (
              'Not scanned yet'
            )}
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <button
            className="tap rounded-[var(--radius-sm)] border border-border px-2 py-1 text-sm disabled:opacity-40"
            onClick={() => refresh.mutate()}
            disabled={status?.refreshing}
            title="Read the news feeds now"
          >
            ⟳ Scan
          </button>
          <button
            className="tap rounded-[var(--radius-sm)] px-2 py-1 text-sm"
            onClick={onClose}
            aria-label="Close Newswire"
          >
            ✕
          </button>
        </div>
      </header>

      <nav
        className="flex gap-1 overflow-x-auto border-b border-border px-2 py-1.5 text-sm"
        style={{ scrollbarWidth: 'none' }}
      >
        {(
          [
            ['fresh', `Fresh ${lists.fresh.length || ''}`],
            ...(mobile || !showSideThreads
              ? [['following', `Following${followingUnseen ? ` · ${followingUnseen} new` : ''}`]]
              : []),
            ['interested', `Saved ${lists.interested.length || ''}`],
            ['dismissed', 'Not for me'],
            ['taste', 'Taste'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="tap shrink-0 rounded-[var(--radius-sm)] px-2.5 py-1"
            style={{
              background: tab === key ? 'var(--surface-2)' : 'transparent',
              fontWeight: tab === key ? 600 : 400,
            }}
          >
            {label.trim()}
          </button>
        ))}
        {!mobile && showSideThreads && followingUnseen > 0 && (
          <span className="ml-auto self-center pr-2 text-xs" style={{ color: 'var(--sat-alert)' }}>
            {followingUnseen} new on stories you follow →
          </span>
        )}
      </nav>

      <div className="relative flex min-h-0 flex-1">
        {dragging && !mobile && <DropBar onDrop={dropOn} />}
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {board.isLoading && <p className="p-4 text-sm text-text-muted">Opening the Newswire…</p>}
          {board.isError && (
            <p className="p-4 text-sm" style={{ color: 'var(--health-4)' }}>
              Couldn't reach the satellite.
            </p>
          )}
          {firstScan && <FirstScan pending={status.pendingDesks} />}
          {!firstScan && data && tab === 'fresh' && lists.fresh.length + lists.muted.length > 0 && (
            <div className="mb-2 flex items-center justify-end gap-2 text-xs">
              {confirmPass ? (
                <>
                  <span className="text-text-muted">Send everything undecided to Not for me?</span>
                  <button
                    className="tap underline"
                    style={{ color: 'var(--health-4)' }}
                    onClick={() => {
                      bulk.mutate({ ids: 'all', action: 'dismissed' });
                      setConfirmPass(false);
                    }}
                  >
                    Pass on all
                  </button>
                  <button className="tap underline" onClick={() => setConfirmPass(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button
                  className="tap underline text-text-muted"
                  onClick={() => setConfirmPass(true)}
                  title="Everything left here goes to Not for me (not counted as a dislike)"
                >
                  ✕ Pass on the rest
                </button>
              )}
            </div>
          )}
          {!firstScan && data && tab === 'fresh' && (
            <Stream
              items={lists.fresh}
              muted={lists.muted}
              onDrag={setDragging}
              act={act}
              undoLast={undoLast}
              markSeen={markSeen}
              mobile={mobile}
              empty="Nothing new. The satellite will look again soon."
            />
          )}
          {data && tab === 'interested' && (
            <Stream
              items={lists.interested}
              onDrag={setDragging}
              act={act}
              undoLast={undoLast}
              markSeen={markSeen}
              mobile={mobile}
              empty="Nothing saved yet. ★ a headline to keep it here."
            />
          )}
          {data && tab === 'dismissed' && <Dismissed items={lists.dismissed} act={act} />}
          {data && tab === 'following' && (
            <Threads threads={threads} draggingItem={dragging} onItemDropped={() => setDragging(null)} mobile={mobile} />
          )}
          {tab === 'taste' && <Taste />}
        </div>
        {showSideThreads && data && (
          <aside
            className="min-h-0 w-[45%] max-w-[22rem] overflow-y-auto border-l border-border px-3 py-3"
            onDragOver={(e) => dragging && e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              dropOn('following');
            }}
            style={{ outline: dragging ? '1px dashed var(--sat-beacon)' : undefined, outlineOffset: -4 }}
          >
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
              Following
            </h3>
            <Threads
              threads={threads}
              compact
              draggingItem={dragging}
              onItemDropped={() => setDragging(null)}
              mobile={mobile}
            />
          </aside>
        )}
      </div>

      {passed && (
        <div className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-text-muted">
          <span>Passed on {passed.length} stories</span>
          <button
            className="tap ml-auto shrink-0 underline"
            onClick={() => bulk.mutate({ ids: passed, action: 'fresh' })}
          >
            Undo
          </button>
          <button className="tap shrink-0" aria-label="Dismiss" onClick={() => setPassed(null)}>
            ✕
          </button>
        </div>
      )}

      {undo[0] && (
        <div className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-text-muted">
          <span className="truncate">Moved “{undo[0].headline}”</span>
          <button className="tap ml-auto shrink-0 underline" onClick={undoLast}>
            Undo (u)
          </button>
        </div>
      )}
    </div>
  );
}

const DROPS: [NewsState, string, string][] = [
  ['interested', '★ Interested', 'var(--health-0)'],
  ['following', '⟳ Follow', 'var(--sat-beacon)'],
  ['dismissed', '✕ Not interested', 'var(--health-4)'],
];

/** Shown while a card is dragged on a desktop: drop it on what you want done with it. */
function DropBar({ onDrop }: { onDrop: (to: NewsState) => void }) {
  const [over, setOver] = useState<NewsState | null>(null);
  return (
    <div
      className="absolute inset-x-0 top-0 z-10 grid grid-cols-3 gap-2 border-b border-border px-3 py-2 shadow-md"
      style={{ background: 'var(--surface-1)' }}
      role="group"
      aria-label="Drop to sort"
    >
      {DROPS.map(([to, label, colour]) => (
        <div
          key={to}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          }}
          onDragEnter={() => setOver(to)}
          onDragLeave={() => setOver((o) => (o === to ? null : o))}
          onDrop={(e) => {
            e.preventDefault();
            onDrop(to);
          }}
          className="grid h-14 place-items-center rounded-[var(--radius-md)] border-2 border-dashed text-sm font-semibold"
          style={{
            borderColor: colour,
            color: colour,
            background: over === to ? `color-mix(in oklch, ${colour} 18%, transparent)` : 'transparent',
          }}
        >
          {label}
        </div>
      ))}
    </div>
  );
}

function FirstScan({ pending }: { pending: string[] }) {
  return (
    <div className="flex flex-col items-center gap-4 px-4 py-10 text-center">
      <div
        className="h-10 w-10 animate-spin rounded-full border-2 border-border"
        style={{ borderTopColor: 'var(--sat-beacon)' }}
        aria-hidden
      />
      <div>
        <p className="font-semibold">The satellite is scanning the world…</p>
        <p className="text-sm text-text-muted">
          The first scan reads every news feed; it takes under a minute.
        </p>
      </div>
      <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-left text-sm">
        {NEWS_DESKS.map((d) => (
          <li key={d}>
            <span style={{ color: pending.includes(d) ? 'var(--text-muted)' : 'var(--health-0)' }}>
              {pending.includes(d) ? '○' : '✓'}
            </span>{' '}
            {NEWS_DESK_LABELS[d]}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stream({
  items,
  muted = [],
  onDrag,
  act,
  undoLast,
  markSeen,
  mobile,
  empty,
}: {
  items: NewsItem[];
  muted?: NewsItem[];
  onDrag?: (item: NewsItem | null) => void;
  act: (item: NewsItem, to: NewsState) => void;
  undoLast: () => void;
  markSeen: (id: string) => void;
  mobile: boolean;
  empty: string;
}) {
  const [focus, setFocus] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [showMuted, setShowMuted] = useState(false);
  const all = useMemo(() => (showMuted ? [...items, ...muted] : items), [showMuted, items, muted]);

  useEffect(() => {
    if (focus >= all.length) setFocus(Math.max(0, all.length - 1));
  }, [all.length, focus]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const current = all[focus];
      if (e.key === 'j') setFocus((f) => Math.min(all.length - 1, f + 1));
      else if (e.key === 'k') setFocus((f) => Math.max(0, f - 1));
      else if (e.key === 'u') undoLast();
      else if (!current) return;
      else if (e.key === 'x') act(current, 'dismissed');
      else if (e.key === 'i') act(current, 'interested');
      else if (e.key === 'f') act(current, 'following');
      else if (e.key === 'Enter') setOpen((o) => (o === current.id ? null : current.id));
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [all, focus, act, undoLast]);

  if (items.length === 0 && muted.length === 0)
    return <p className="p-4 text-sm text-text-muted">{empty}</p>;

  return (
    <ol className="flex flex-col gap-2">
      {items.map((item, i) => (
        <Card
          key={item.id}
          item={item}
          focused={i === focus}
          open={open === item.id}
          onToggle={() => setOpen((o) => (o === item.id ? null : item.id))}
          onFocus={() => setFocus(i)}
          act={act}
          onDrag={onDrag}
          markSeen={markSeen}
          mobile={mobile}
        />
      ))}
      {muted.length > 0 && (
        <li>
          <button
            className="tap w-full py-2 text-left text-xs text-text-muted"
            onClick={() => setShowMuted((s) => !s)}
          >
            {showMuted ? '▾' : '▸'} Probably not for you ({muted.length})
          </button>
        </li>
      )}
      {showMuted &&
        muted.map((item, j) => (
          <Card
            key={item.id}
            item={item}
            focused={items.length + j === focus}
            open={open === item.id}
            onToggle={() => setOpen((o) => (o === item.id ? null : item.id))}
            onFocus={() => setFocus(items.length + j)}
            act={act}
            onDrag={onDrag}
            markSeen={markSeen}
            mobile={mobile}
            dim
          />
        ))}
    </ol>
  );
}

function Card({
  item,
  focused,
  open,
  onToggle,
  onFocus,
  act,
  onDrag,
  markSeen,
  mobile,
  dim = false,
}: {
  item: NewsItem;
  focused: boolean;
  open: boolean;
  onToggle: () => void;
  onFocus: () => void;
  act: (item: NewsItem, to: NewsState) => void;
  onDrag?: ((item: NewsItem | null) => void) | undefined;
  markSeen: (id: string) => void;
  mobile: boolean;
  dim?: boolean;
}) {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (item.seen || !ref.current || typeof IntersectionObserver === 'undefined') return;
    let timer: number | null = null;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) timer = window.setTimeout(() => markSeen(item.id), 1500);
        else if (timer) window.clearTimeout(timer);
      },
      { threshold: 0.6 },
    );
    obs.observe(ref.current);
    return () => {
      obs.disconnect();
      if (timer) window.clearTimeout(timer);
    };
  }, [item.id, item.seen, markSeen]);

  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [focused]);

  // Phone swipe: left = not interested, right = interested, hold = follow.
  const drag = useRef<{ x: number; y: number; held: number | null } | null>(null);
  const [dx, setDx] = useState(0);
  const onDown = (e: ReactPointerEvent) => {
    if (!mobile) return;
    const held = window.setTimeout(() => {
      drag.current = null;
      setDx(0);
      act(item, 'following');
    }, 650);
    drag.current = { x: e.clientX, y: e.clientY, held };
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const mx = e.clientX - d.x;
    if (Math.abs(mx) > 8 || Math.abs(e.clientY - d.y) > 8) {
      if (d.held) window.clearTimeout(d.held);
      d.held = null;
    }
    if (Math.abs(e.clientY - d.y) > Math.abs(mx)) return;
    setDx(mx);
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.held) window.clearTimeout(d.held);
    if (dx < -90) act(item, 'dismissed');
    else if (dx > 90) act(item, 'interested');
    setDx(0);
  };

  const outlets = [...new Set(item.sources.map((s) => s.outlet))];

  return (
    <li
      ref={ref}
      onMouseEnter={onFocus}
      className="rounded-[var(--radius-md)] border px-3 py-2"
      style={{
        borderColor: focused ? 'var(--sat-beacon)' : 'var(--border)',
        background: 'var(--surface-0, transparent)',
        opacity: dim ? 0.7 : 1,
        transform: dx ? `translateX(${dx}px)` : undefined,
        touchAction: mobile ? 'pan-y' : undefined,
      }}
      draggable={!mobile && !!onDrag}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', item.id);
        e.dataTransfer.effectAllowed = 'move';
        // Next tick: changing the page inside dragstart can cancel the drag in Chrome.
        window.setTimeout(() => onDrag?.(item), 0);
      }}
      onDragEnd={() => onDrag?.(null)}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <div className="flex items-start gap-2">
        <button className="min-w-0 flex-1 text-left" onClick={onToggle} aria-expanded={open}>
          <div className="mb-0.5 flex items-center gap-2 whitespace-nowrap text-[11px] text-text-muted">
            <span
              className="rounded px-1.5 py-px font-semibold"
              style={{
                color: deskColour(item.desk),
                background: `color-mix(in oklch, ${deskColour(item.desk)} 14%, transparent)`,
              }}
            >
              {NEWS_DESK_LABELS[item.desk]}
            </span>
            <span>{ago(item.publishedAt ?? item.createdAt)}</span>
            <span>
              · {item.sources.length} source{item.sources.length === 1 ? '' : 's'}
            </span>
            {!item.seen && <span style={{ color: 'var(--sat-beacon)' }}>● new</span>}
          </div>
          <div className="font-semibold leading-snug">{item.headline}</div>
        </button>
        <div className="flex shrink-0 gap-0.5">
          <ActionButton
            label="Not interested (x)"
            active={item.state === 'dismissed'}
            onClick={() => act(item, item.state === 'dismissed' ? 'fresh' : 'dismissed')}
          >
            ✕
          </ActionButton>
          <ActionButton
            label="Interested (i)"
            active={item.state === 'interested'}
            onClick={() => act(item, item.state === 'interested' ? 'fresh' : 'interested')}
          >
            ★
          </ActionButton>
          <ActionButton
            label="Follow (f)"
            active={item.state === 'following'}
            onClick={() => act(item, item.state === 'following' ? 'interested' : 'following')}
          >
            ⟳
          </ActionButton>
        </div>
      </div>
      {open && (
        <div className="mt-2 flex flex-col gap-1.5 text-sm">
          {item.dek && <p>{item.dek}</p>}
          {item.whyItMatters ? (
            <p className="text-text-muted">Why it matters: {item.whyItMatters}</p>
          ) : (
            <Explain item={item} />
          )}
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {item.sources.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline"
                  style={{ color: 'var(--sat-beacon)' }}
                >
                  {s.outlet || new URL(s.url).hostname}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!open && outlets.length > 0 && (
        <div className="mt-0.5 truncate text-[11px] text-text-muted">{outlets.join(' · ')}</div>
      )}
    </li>
  );
}

/** Whether the cheap model is on, from the shared board query. */
function useAiOn(): boolean {
  const board = useQuery({ queryKey: BOARD_KEY, queryFn: fetchNewsBoard, select: (d) => d.status.ai });
  return board.data ?? false;
}

/** Explain: asks once; the answer is stored with the story, so it is instant ever after. */
function Explain({ item }: { item: NewsItem }) {
  const queryClient = useQueryClient();
  const ai = useAiOn();
  const explain = useMutation({
    mutationFn: () => explainNews(item.id),
    onSuccess: ({ whyItMatters }) =>
      queryClient.setQueryData<NewsBoard>(BOARD_KEY, (old) =>
        old
          ? { ...old, items: old.items.map((i) => (i.id === item.id ? { ...i, whyItMatters } : i)) }
          : old,
      ),
  });
  if (!ai) return null;
  return (
    <div className="text-xs">
      <button
        className="tap underline disabled:opacity-40"
        style={{ color: 'var(--sat-beacon)' }}
        disabled={explain.isPending}
        onClick={(e) => {
          e.stopPropagation();
          explain.mutate();
        }}
      >
        {explain.isPending ? 'Explaining…' : 'Explain why it matters'}
      </button>
      {explain.isError && <span className="ml-2 text-text-muted">Couldn't explain just now.</span>}
    </div>
  );
}

function ActionButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className="tap grid h-8 w-8 place-items-center rounded-[var(--radius-sm)] text-sm"
      style={{
        background: active ? 'var(--surface-2)' : 'transparent',
        color: active ? 'var(--sat-beacon)' : undefined,
      }}
    >
      {children}
    </button>
  );
}

function Dismissed({
  items,
  act,
}: {
  items: NewsItem[];
  act: (item: NewsItem, to: NewsState) => void;
}) {
  const [q, setQ] = useState('');
  const shown = q.trim()
    ? items.filter((i) => `${i.headline} ${i.dek}`.toLowerCase().includes(q.trim().toLowerCase()))
    : items;
  return (
    <div className="flex flex-col gap-2">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search what you passed on"
        className="rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1.5 text-sm"
      />
      {shown.length === 0 && <p className="p-2 text-sm text-text-muted">Nothing here.</p>}
      <ul className="flex flex-col">
        {shown.map((i) => (
          <li key={i.id} className="flex items-center gap-2 border-b border-border py-1.5 text-sm">
            <span className="text-[11px]" style={{ color: deskColour(i.desk) }}>
              ●
            </span>
            <span className="min-w-0 flex-1 truncate text-text-muted">{i.headline}</span>
            <button className="tap shrink-0 text-xs underline" onClick={() => act(i, 'fresh')}>
              Restore
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Threads({
  threads,
  compact = false,
  draggingItem = null,
  onItemDropped,
  mobile = false,
}: {
  threads: NewsThread[];
  compact?: boolean;
  /** A headline being dragged (desktop): dropped on a story, it is followed into that story's group. */
  draggingItem?: NewsItem | null;
  onItemDropped?: () => void;
  mobile?: boolean;
}) {
  // A followed story being dragged onto another, to group them (desktop).
  const [draggingThread, setDraggingThread] = useState<string | null>(null);
  if (threads.length === 0)
    return (
      <p className="text-sm text-text-muted">
        {compact
          ? 'Follow (⟳) a story to track every development here.'
          : 'No followed stories yet. ⟳ a headline to follow it.'}
      </p>
    );
  return (
    <>
      {threads.length > 1 && !mobile && (
        <p className="mb-2 text-[11px] text-text-muted">Drag one story onto another to group them.</p>
      )}
      <ul className="flex flex-col gap-2">
        {threads.map((t) => (
          <ThreadCard
            key={t.id}
            thread={t}
            others={threads.filter((o) => o.id !== t.id)}
            compact={compact}
            mobile={mobile}
            draggingItem={draggingItem}
            draggingThread={draggingThread}
            onDragThread={setDraggingThread}
            onItemDropped={onItemDropped}
          />
        ))}
      </ul>
    </>
  );
}

function ThreadCard({
  thread,
  others,
  compact,
  mobile,
  draggingItem,
  draggingThread,
  onDragThread,
  onItemDropped,
}: {
  thread: NewsThread;
  others: NewsThread[];
  compact: boolean;
  mobile: boolean;
  draggingItem: NewsItem | null;
  draggingThread: string | null;
  onDragThread: (id: string | null) => void;
  onItemDropped: (() => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(!compact && thread.unseen > 0);
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
    void queryClient.invalidateQueries({ queryKey: NEWS_STATUS_KEY });
  };
  const check = useMutation({ mutationFn: () => checkThreadNow(thread.id), onSettled: invalidate });
  const unfollow = useMutation({
    mutationFn: () => unfollowThread(thread.id),
    onSettled: invalidate,
  });
  const merge = useMutation({
    mutationFn: ({ from, into }: { from: string; into: string }) => mergeThreads(from, into),
    onSettled: invalidate,
  });
  const absorb = useMutation({
    mutationFn: (itemId: string) => followInto(itemId, thread.id),
    onSettled: invalidate,
  });
  const split = useMutation({ mutationFn: (id: string) => splitThread(id), onSettled: invalidate });
  const [naming, setNaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const rename = useMutation({
    mutationFn: (label: string) => setGroupLabel(thread.id, label),
    onSuccess: () => setNaming(false),
    onSettled: invalidate,
  });

  useEffect(() => {
    if (open && thread.unseen > 0) void markThreadSeen(thread.id).then(invalidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, thread.unseen, thread.id]);

  const stale = thread.lastError
    ? `last check failed ${ago(thread.lastCheckedAt) || ''} — retrying next scan`
    : null;
  const [editing, setEditing] = useState<string | null>(null);
  const [termsDraft, setTermsDraft] = useState('');
  const saveTerms = useMutation({
    mutationFn: ({ id, terms }: { id: string; terms: string[] }) => setThreadTerms(id, terms),
    onSuccess: () => setEditing(null),
    onSettled: invalidate,
  });

  const grouped = thread.members.length > 0;
  const name = thread.label ?? thread.title;
  const stories = [{ id: thread.id, title: thread.title, terms: thread.terms }, ...thread.members];
  const titleOf = new Map(stories.map((s) => [s.id, s.title]));
  const [over, setOver] = useState(false);
  const droppable = (draggingItem && draggingItem.threadId !== thread.id) || (draggingThread && draggingThread !== thread.id);

  return (
    <li
      className="rounded-[var(--radius-md)] border px-3 py-2"
      style={{
        borderColor: over ? 'var(--sat-beacon)' : thread.unseen ? 'var(--sat-alert)' : 'var(--border)',
        background: over ? 'color-mix(in oklch, var(--sat-beacon) 12%, transparent)' : undefined,
        opacity: draggingThread === thread.id ? 0.5 : 1,
      }}
      draggable={!mobile}
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData('text/plain', thread.id);
        e.dataTransfer.effectAllowed = 'move';
        window.setTimeout(() => onDragThread(thread.id), 0);
      }}
      onDragEnd={() => onDragThread(null)}
      onDragOver={(e) => {
        if (!droppable) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={() => droppable && setOver(true)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => {
        if (!droppable) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        if (draggingThread) merge.mutate({ from: draggingThread, into: thread.id });
        else if (draggingItem) absorb.mutate(draggingItem.id);
        onDragThread(null);
        onItemDropped?.();
      }}
    >
      {over && (
        <div className="mb-1 text-[11px] font-semibold" style={{ color: 'var(--sat-beacon)' }}>
          Drop to group with this story
        </div>
      )}
      <button className="w-full text-left" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 font-semibold leading-snug">{name}</span>
          {thread.unseen > 0 && (
            <span
              className="shrink-0 rounded px-1.5 text-xs font-semibold"
              style={{ background: 'var(--sat-alert)', color: 'var(--space-deep)' }}
            >
              {thread.unseen} new
            </span>
          )}
        </div>
        <div className="text-[11px] text-text-muted">
          {grouped && `${thread.members.length + 1} stories · `}
          {thread.updates.length} development{thread.updates.length === 1 ? '' : 's'}
          {thread.lastUpdateAt && ` · latest ${ago(thread.lastUpdateAt)} ago`}
          {thread.lastCheckedAt && ` · checked ${ago(thread.lastCheckedAt)} ago`}
        </div>
      </button>
      {stale && (
        <p className="mt-1 text-[11px]" style={{ color: 'var(--health-2)' }}>
          {stale}
        </p>
      )}
      {open && (
        <div className="mt-2 flex flex-col gap-2 text-sm">
          {thread.brief && !grouped && <p className="text-xs text-text-muted">{thread.brief}</p>}
          {naming ? (
            <form
              className="flex gap-1 text-xs"
              onSubmit={(e) => {
                e.preventDefault();
                rename.mutate(nameDraft.trim());
              }}
            >
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                aria-label="Name this story"
                className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1"
              />
              <button className="tap rounded-[var(--radius-sm)] border border-border px-2">Save</button>
            </form>
          ) : (
            grouped && (
              <button
                className="tap self-start text-xs underline text-text-muted"
                onClick={() => {
                  setNameDraft(thread.label ?? '');
                  setNaming(true);
                }}
              >
                {thread.label ? 'Rename' : 'Name this group'}
              </button>
            )
          )}
          <ul className="flex flex-col gap-1 text-xs text-text-muted">
            {stories.map((s) => (
              <li key={s.id}>
                {grouped && <div className="font-medium text-text">{s.title}</div>}
                {editing === s.id ? (
                  <form
                    className="flex gap-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveTerms.mutate({ id: s.id, terms: termsDraft.split(/[\s,]+/).filter(Boolean) });
                    }}
                  >
                    <input
                      value={termsDraft}
                      onChange={(e) => setTermsDraft(e.target.value)}
                      aria-label="Words to watch for"
                      className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1"
                    />
                    <button className="tap rounded-[var(--radius-sm)] border border-border px-2">Save</button>
                  </form>
                ) : (
                  <p>
                    Watching for: {s.terms.join(', ') || 'nothing yet'}{' '}
                    <button
                      className="tap underline"
                      onClick={() => {
                        setTermsDraft(s.terms.join(' '));
                        setEditing(s.id);
                      }}
                    >
                      Edit
                    </button>
                    {grouped && (
                      <>
                        {' · '}
                        <button className="tap underline" onClick={() => split.mutate(s.id)}>
                          Take out of group
                        </button>
                      </>
                    )}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <ol className="flex flex-col gap-2 border-l border-border pl-3">
            {thread.updates.map((u) => (
              <li key={u.id}>
                <div className="text-[11px] text-text-muted">
                  {new Date(u.publishedAt ?? u.createdAt).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {grouped && ` · ${titleOf.get(u.threadId) ?? ''}`}
                  {!u.seen && <span style={{ color: 'var(--sat-alert)' }}> ● new</span>}
                </div>
                <div className="font-semibold leading-snug">{u.headline}</div>
                {u.body && <p className="text-xs">{u.body}</p>}
                <div className="flex flex-wrap gap-x-3 text-xs">
                  {u.sources.map((s) => (
                    <a
                      key={s.url}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline"
                      style={{ color: 'var(--sat-beacon)' }}
                    >
                      {s.outlet}
                    </a>
                  ))}
                </div>
              </li>
            ))}
            {thread.updates.length === 0 && (
              <li className="text-xs text-text-muted">
                No developments yet. Checked on every scan.
              </li>
            )}
          </ol>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <button
              className="tap underline disabled:opacity-40"
              disabled={check.isPending}
              onClick={() => check.mutate()}
            >
              {check.data?.started ? 'Checking…' : 'Check now'}
            </button>
            <button className="tap underline" onClick={() => unfollow.mutate()}>
              {grouped ? 'Unfollow all' : 'Unfollow'}
            </button>
            {others.length > 0 && (
              <select
                className="rounded-[var(--radius-sm)] border border-border bg-transparent px-1 py-0.5"
                aria-label="Group with another story"
                value=""
                onChange={(e) => e.target.value && merge.mutate({ from: thread.id, into: e.target.value })}
              >
                <option value="">Group with…</option>
                {others.map((o) => (
                  <option key={o.id} value={o.id}>
                    {(o.label ?? o.title).length > 60 ? `${(o.label ?? o.title).slice(0, 57)}…` : (o.label ?? o.title)}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function Taste() {
  const queryClient = useQueryClient();
  const taste = useQuery({ queryKey: ['news-taste'], queryFn: fetchNewsTaste });
  const [draft, setDraft] = useState('');
  const [polarity, setPolarity] = useState<'more' | 'less'>('more');
  const save = useMutation({
    mutationFn: (rules: NewsRule[]) => saveNewsRules(rules),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['news-taste'] }),
  });
  const reset = useMutation({
    mutationFn: resetNewsTaste,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['news-taste'] });
      void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
    },
  });
  const [confirmReset, setConfirmReset] = useState(false);
  const rules = taste.data?.rules ?? [];

  return (
    <div className="flex flex-col gap-4 text-sm">
      <section>
        <h3 className="mb-1 font-semibold">Your rules</h3>
        <p className="mb-2 text-xs text-text-muted">
          Keyword filters: a story whose headline has every word of a rule is lifted (More) or tucked
          under “Probably not for you” (Less). The ranking also learns from your{' '}
          {taste.data?.decisions ?? 0} decisions, with no AI.
        </p>
        <ul className="flex flex-col gap-1">
          {rules.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-border px-2 py-1"
            >
              <button
                className="tap shrink-0 text-xs font-semibold"
                style={{ color: r.polarity === 'more' ? 'var(--health-0)' : 'var(--health-4)' }}
                title="Switch between more and less"
                onClick={() =>
                  save.mutate(
                    rules.map((x) =>
                      x.id === r.id
                        ? {
                            ...x,
                            polarity: x.polarity === 'more' ? 'less' : 'more',
                            source: 'user',
                          }
                        : x,
                    ),
                  )
                }
              >
                {r.polarity === 'more' ? 'MORE' : 'LESS'}
              </button>
              <span className="min-w-0 flex-1">{r.text}</span>
              <button
                className="tap shrink-0 text-xs"
                aria-label="Remove rule"
                onClick={() => save.mutate(rules.filter((x) => x.id !== r.id))}
              >
                ✕
              </button>
            </li>
          ))}
          {rules.length === 0 && (
            <li className="text-xs text-text-muted">
              No rules yet. Add one below, e.g. Less: “Strictly”.
            </li>
          )}
        </ul>
        <form
          className="mt-2 flex gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.trim()) return;
            save.mutate([
              ...rules,
              { id: crypto.randomUUID(), text: draft.trim(), polarity, source: 'user' },
            ]);
            setDraft('');
          }}
        >
          <select
            value={polarity}
            onChange={(e) => setPolarity(e.target.value as 'more' | 'less')}
            className="rounded-[var(--radius-sm)] border border-border bg-transparent px-1 text-xs"
          >
            <option value="more">More</option>
            <option value="less">Less</option>
          </select>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. AWS pricing"
            className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1"
          />
          <button className="tap rounded-[var(--radius-sm)] border border-border px-2">Add</button>
        </form>
      </section>

      {taste.data && taste.data.topTerms.length > 0 && (
        <section>
          <h3 className="mb-1 font-semibold">Strongest signals</h3>
          <div className="flex flex-wrap gap-1">
            {taste.data.topTerms.map((t) => (
              <span
                key={t.term}
                className="rounded px-1.5 py-0.5 text-xs"
                style={{
                  background: 'var(--surface-2)',
                  color: t.weight > 0 ? 'var(--health-0)' : 'var(--health-4)',
                }}
              >
                {t.weight > 0 ? '+' : '−'} {t.term}
              </span>
            ))}
          </div>
        </section>
      )}

      <section>
        {confirmReset ? (
          <div className="flex items-center gap-2 text-xs">
            <span>Forget everything learned? Your decisions stay on record.</span>
            <button
              className="tap underline"
              style={{ color: 'var(--health-4)' }}
              onClick={() => {
                reset.mutate();
                setConfirmReset(false);
              }}
            >
              Forget
            </button>
            <button className="tap underline" onClick={() => setConfirmReset(false)}>
              Keep
            </button>
          </div>
        ) : (
          <button
            className="tap text-xs underline text-text-muted"
            onClick={() => setConfirmReset(true)}
          >
            Reset what it has learned
          </button>
        )}
      </section>
    </div>
  );
}
